/**
 * Encaptulates functionality to store and retrieve object data from the ES data store
 */
import { ESInterface } from "src/compentsModus/ESInterface";

export class WorkQueue {
   private lastJob: Promise<void> | undefined

   async queueWork(work: () => Promise<void>): Promise<void> {
      const nextJob = this.lastJob = this.lastJob?.then(work, _ => { }) ?? work()
      try {
         return await nextJob
      } finally {
         // ensure previous results can be garbage collected
         if (nextJob === this.lastJob) { this.lastJob = undefined }
      }
   }
}

export class ObjectVersionRegister {
   objectVersions = new Map<string, number>();

   getObjectVersion(objectId: string): number {
      return this.objectVersions.get(objectId) || 0;
   }

   setObjectVersion(objectId: string, version: number) {
      if (version < (this.objectVersions.get(objectId) || 0)) {
         return;
      }
      this.objectVersions.set(objectId, version);
   }

   clearObjectVersion(objectId: string) {
      this.objectVersions.delete(objectId);
   }

   clearAllObjectVersions() {
      this.objectVersions.clear();
   }
}

export class OptimisticLockingConflictError extends Error {
   constructor(message: string, public readonly documentId: string, public readonly currentDocument: any, public readonly operation: "update" | "delete") {
      super(message);
      this.name = "OptimisticLockingConflictError";
   }
}

export class OptimisticLockingWrapper {
   es: ESInterface;
   versionRegister: ObjectVersionRegister;
   getIdColumn: (data: any) => string
   workQueue = new WorkQueue();
   userId?: string;

   constructor(es: ESInterface, versionRegister: ObjectVersionRegister, getIdColumn: (data: any) => string, userId?: string) {
      this.es = es;
      this.versionRegister = versionRegister;
      this.getIdColumn = getIdColumn;
      this.userId = userId;
   }

   async updateDocument(documentId: string, document: any) {
      try {
         await this.workQueue.queueWork(() => this._updateDocument(documentId, document));
         // this._updateDocument(documentId, document);
      }
      catch (error: any) {
         console.log("Error in updateDocument: " + error.message);
         throw error;
      }
   }

   async _updateDocument(documentId: string, document: any) {
      await this.ensureCurrentVersion(documentId, "update");
      document.viamapUserId = this.userId;
      let record = await this.es.updateDocument(documentId, document);
      if (record._version) this.versionRegister.setObjectVersion(documentId, record._version);
   }

   async ensureCurrentVersion(documentId: string, operation: "update" | "delete") {
      let versionNumberWhenRead = this.versionRegister.getObjectVersion(documentId);
      if (!versionNumberWhenRead) return;
      // Check if the version number is still the same. If not, return an error.
      let currentDb = await this.es.getDocument(documentId);
      versionNumberWhenRead = this.versionRegister.getObjectVersion(documentId);
      if (currentDb && currentDb._version != versionNumberWhenRead) {
         let doc = { _version: currentDb._version, ...currentDb._source };
         if (currentDb._version) this.versionRegister.setObjectVersion(documentId, currentDb._version);
         throw new OptimisticLockingConflictError(`A newer version of this document exists, by user ${doc.viamapUserId}. Cannot ${operation}.`, documentId, doc, operation);
      }
   }

   async getDocument(documentId: string) {
      let record = await this.es.getDocument(documentId);
      if (record._version) this.versionRegister.setObjectVersion(documentId, record._version);
      return { _version: record._version, ...record._source };
   }

   async deleteDocument(documentId: string) {
      await this.ensureCurrentVersion(documentId, "delete");
      await this.es.deleteDocument(documentId);
      this.versionRegister.clearObjectVersion(documentId);
   }

   async listDocuments() {
      let res = await this.es.doSearch({}, {}, 5000);
      if (res && res.length > 0) {
         res.forEach((record: any) => {
            if (record._version) this.versionRegister.setObjectVersion(this.getIdColumn(record), record._version);
         });
      }
      return res;
   }

   async emptyTheIndex(indexName: string) {
      await this.es.emptyTheIndex(indexName);
      this.versionRegister.clearAllObjectVersions();
   }

}

export interface VersionedObjectPersistenceInterface<Type> {
   listObjects(filter: any): Promise<Type[]>;
   getObject(objectId: string): Promise<Type>;
   saveObject(object: Type): Promise<void>;
   deleteObject(objectId: string): Promise<void>;
   createObject(object: Type): Promise<void>
};

export class VersionedObjectPersistence<Type> implements VersionedObjectPersistenceInterface<Type> {

   esIndexName: string;
   es: ESInterface;
   versions: ObjectVersionRegister;
   dw: OptimisticLockingWrapper;
   indexMappings: any = {
      "properties": {
         // Other fields are dynamic and assigned a type automatically by ES
      }
   };
   idGetter: (data: Type) => string;
   userId: string;

   static instance: VersionedObjectPersistence<any>;

   static setInstance(instance: VersionedObjectPersistence<any>) {
      VersionedObjectPersistence.instance = instance;
   }

   static getInstance(): VersionedObjectPersistence<any> {
      if (!VersionedObjectPersistence.instance) {
         throw new Error("VersionedObjectPersistence not initialized");
      }
      return VersionedObjectPersistence.instance;
   }
   /* Example idGetter(object) => { return object.id; } */
   constructor(objectsESIndexName: string, userId: string, indexMappings: any, idGetter: (data: Type) => string) {
      this.esIndexName = objectsESIndexName;
      this.es = new ESInterface(this.esIndexName)
      this.versions = new ObjectVersionRegister();
      this.dw = new OptimisticLockingWrapper(this.es, this.versions, (data) => data.id, userId);
      this.userId = userId;
      this.indexMappings = indexMappings;
      this.idGetter = idGetter;
   }

   async listObjects(): Promise<Type[]> {
      // Retrieve objects from the data store
      let res = await this.dw.listDocuments();
      if (res && res.length > 0) {
         return res as Type[];
      } else {
         return [];
      }
   }

   async getObject(objectId: string): Promise<Type> {
      return await this.dw.getDocument(objectId);
   }

   async saveObject(object: Type): Promise<void> {
      return await this.dw.updateDocument(this.idGetter(object), object);
   }

   async createObject(object: Type): Promise<void> {
      // Save object to the data store
      this.saveObject(object)
         .then(() => {
            // Create sfe/lodsejer database for object

            this.es.createIndex(this.esIndexName, { mappings: { ...(this.indexMappings), "_meta": { "CreatedBy": this.userId } } })
               .catch((error) => {
                  alert("Got error " + (error.message || error));
               })
         })
   }

   async deleteObject(objectId: string): Promise<void> {
      // Save object to the data store
      await this.dw.deleteDocument(objectId);
   }

   async removeObjects(): Promise<void> {
      await this.dw.emptyTheIndex(this.esIndexName);
   }
}